import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminWallets } from '../../Hooks/Admin/useAdminWallets';
import FilterInput from '../../Components/FilterInput';
import { FaSearch, FaSync, FaCrown } from 'react-icons/fa';
import Alert from '../../Components/Alert';
import Pagination from '../../Components/Pagination';
import { formatDate } from '../../Utils/dateFormatter';



const AdminWalletsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        wallets,
        stats,
        total,
        loading,
        statsLoading,
        error,
        filters,
        setFilters,
        setPage,
        refresh
    } = useAdminWallets();

    // Sync URL → hook page on first render
    useEffect(() => {
        const urlPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
        if (urlPage !== 1) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePageChange = (newPage) => {
        setPage(newPage);
        setSearchParams(prev => { prev.set('page', String(newPage)); return prev; }, { replace: true });
    };

    const [localSearch, setLocalSearch] = useState('');

    const handleSearch = () => {
        setFilters(prev => ({ ...prev, search: localSearch, page: 1 }));
    };

    return (
        <div className="p-6 bg-light min-h-screen font-cairo dir-rtl">
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <h2 className="text-2xl font-bold text-main1">إحصائيات ومحافظ المستخدمين</h2>
                    <button
                        onClick={refresh}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-main1 text-white hover:bg-opacity-90 transition-all shadow-sm"
                    >
                        <FaSync className={loading || statsLoading ? "animate-spin" : ""} />
                        <span>تحديث البيانات</span>
                    </button>
                </div>
            </div>

            {/* Statistics Section as a Simple Table */}
            <div className="overflow-x-auto rounded-2xl shadow-md bg-transparent mb-8">
                <table className="min-w-full text-sm font-cairo text-center text-main1">
                    <thead>
                        <tr className="bg-grayish text-main1 h-12">
                            <th className="p-4 font-bold">إجمالي الأرصدة</th>
                            <th className="p-4 font-bold">إجمالي المحافظ</th>
                            <th className="p-4 font-bold">المحافظ النشطة</th>
                            <th className="p-4 font-bold">محافظ برصيد</th>
                            <th className="p-4 font-bold">عدد التجار</th>
                        </tr>
                    </thead>
                    <tbody className="text-main1">
                        {statsLoading ? (
                            <tr>
                                <td colSpan="5" className="p-6">
                                    <div className="flex justify-center text-gray-400">
                                        جاري تحميل الإحصائيات...
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            <tr className="hover:bg-grayish/60 transition">
                                <td className="p-5 font-bold text-main2 text-lg">{Number(stats?.total_balance || 0).toLocaleString()} <span className="text-xs">ليرة سورية</span></td>
                                <td className="p-5 font-bold text-lg">{stats?.statistics?.total_wallets || 0}</td>
                                <td className="p-5 font-bold text-green-600 text-lg">{stats?.statistics?.active_wallets || 0}</td>
                                <td className="p-5 font-bold text-blue-600 text-lg">{stats?.statistics?.wallets_with_balance || 0}</td>
                                <td className="p-5 font-bold text-orange-600 text-lg">{stats?.statistics?.merchants || 0}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                {/* Main Wallets Table Section */}
                <div className="xl:col-span-2 space-y-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="w-full sm:w-1/2 relative">
                            <FilterInput
                                type="text"
                                placeholder="بحث برقم الهاتف أو اسم المالك..."
                                value={localSearch}
                                onChange={(e) => setLocalSearch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                className="w-full pl-10 pr-4 shadow-sm border-gray-200"
                            />
                            <button
                                onClick={handleSearch}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-main1"
                            >
                                <FaSearch />
                            </button>
                        </div>
                        <div className="text-gray-500 text-sm">
                            إجمالي المسجلين: <span className="font-bold text-main1">{total}</span>
                        </div>
                    </div>

                    {error && (
                        <Alert type="error" className="mb-4">
                            {typeof error === 'string' ? error : 'فشل في تحميل بيانات المحافظ'}
                        </Alert>
                    )}

                    <div className="overflow-x-auto rounded-2xl shadow-md bg-transparent border border-white/5">
                        <table className="min-w-full text-sm text-main1 font-cairo">
                            <thead>
                                <tr className="bg-grayish text-main1 h-14">
                                    <th className="p-4 font-bold text-center">المعرف</th>
                                    <th className="p-4 font-bold text-center">مالك المحفظة</th>
                                    <th className="p-4 font-bold text-center">رقم الهاتف</th>
                                    <th className="p-4 font-bold text-center">الرصيد</th>
                                    <th className="p-4 font-bold text-center">الحالة</th>
                                    <th className="p-4 font-bold text-center">تاريخ الإنشاء</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-16">
                                            <div className="flex flex-col items-center gap-4">
                                                <FaSync className="animate-spin text-main1 text-3xl" />
                                                <span className="text-gray-500 font-medium">جاري تحميل المحافظ...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : wallets.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-16">
                                            <span className="text-gray-500">لا توجد سجلات متاحة</span>
                                        </td>
                                    </tr>
                                ) : (
                                    wallets.map((wallet) => (
                                        <tr key={wallet.id} className="border-b border-white/5 last:border-b-0 hover:bg-grayish/60 transition">
                                            <td className="p-4 text-center text-gray-400 font-mono">#{wallet.user_id}</td>
                                            <td className="p-4 font-medium text-center">{wallet.wallet_owner || '-'}</td>
                                            <td className="p-4 font-mono text-center">{wallet.phone_number || '-'}</td>
                                            <td className="p-4 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <span className="text-main1 font-bold text-base">
                                                        {Number(wallet.balance).toLocaleString()}
                                                    </span>
                                                    <span className="text-xs text-gray-400">ل.س</span>
                                                </div>
                                            </td>
                                            <td className="p-4 text-center">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 justify-center w-fit mx-auto ${wallet.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                    {wallet.is_active ? 'نشط' : 'معطل'}
                                                </span>
                                            </td>
                                            <td className="p-4 text-center text-gray-400 text-[10px] whitespace-pre">
                                                {formatDate(wallet.created_at)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={filters.page}
                        totalPages={Math.ceil(total / 20)}
                        onPageChange={handlePageChange}
                        totalItems={total}
                        itemsPerPage={20}
                        loading={loading}
                    />
                </div>

                {/* Top Wallets Side Section */}
                <div className="xl:col-span-1">
                    <div className="bg-transparent rounded-2xl shadow-md overflow-hidden">
                        {/* Header */}
                        <div className="bg-grayish p-5 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-main2/10 flex items-center justify-center text-main2">
                                <FaCrown size={20} />
                            </div>
                            <h3 className="text-lg font-bold text-main1">أعلى أرصدة المحافظ</h3>
                        </div>

                        {/* List */}
                        <div>
                            {statsLoading ? (
                                <div className="p-10 flex justify-center">
                                    <FaSync className="animate-spin text-main1" />
                                </div>
                            ) : stats?.top_wallets?.length > 0 ? (
                                stats.top_wallets.map((wallet, index) => (
                                    <div key={wallet.user_id} className="p-4 flex items-center justify-between border-b border-white/5 last:border-b-0 hover:bg-grayish/60 transition duration-300">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shadow-sm ${index === 0 ? 'bg-amber-100 text-amber-600' :
                                                index === 1 ? 'bg-gray-200 text-gray-700' :
                                                    index === 2 ? 'bg-orange-100 text-orange-600' :
                                                        'bg-grayish text-gray-500'
                                                }`}>
                                                {index + 1}
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-main1">
                                                    {wallet.user_name}
                                                </span>
                                                <span className="text-[10px] text-gray-500 font-mono">
                                                    {wallet.user_phone}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end">
                                            <span className="text-sm font-bold text-main2">
                                                {Number(wallet.balance).toLocaleString()}
                                                <span className="text-[10px] mr-1">S.P</span>
                                            </span>
                                            <span className={`text-[10px] font-medium ${wallet.is_active ? 'text-green-600' : 'text-red-500'}`}>
                                                {wallet.is_active ? 'نشط' : 'معطل'}
                                            </span>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-8 text-center text-gray-500 text-sm">
                                    لا توجد بيانات متاحة
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminWalletsPage;
